import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { apiClient } from '@/lib/api'

type Props = {
  open: boolean
  onClose: () => void
}

export default function SearchModal({ open, onClose }: Props) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [term, setTerm] = useState('')
  const [loading, setLoading] = useState(false)
  const [results, setResults] = useState<Array<{ uuid: string; identifier: string; name: string; node: string; allocation?: { ip: string; ip_alias: string | null; port: number } }>>([])

  useEffect(() => {
    if (!open) return
    const t = setTimeout(() => inputRef.current?.focus(), 0)
    return () => clearTimeout(t)
  }, [open])

  useEffect(() => {
    if (!open) return
    if (term.trim().length < 3) {
      setResults([])
      return
    }
    let cancelled = false
    setLoading(true)
    const id = setTimeout(async () => {
      try {
        const list = await apiClient.searchServers(term, 5)
        if (!cancelled) setResults(list)
      } catch {
        if (!cancelled) setResults([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }, 400)
    return () => { cancelled = true; clearTimeout(id) }
  }, [term, open])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="absolute left-1/2 top-24 w-full max-w-xl -translate-x-1/2">
        <div className="rounded-xl border border-gray-200 dark:border-[#2d2d2d] bg-white dark:bg-[#252526] shadow-xl overflow-hidden">
          <div className="p-3 border-b border-gray-100 dark:border-[#2d2d2d]">
            <input
              ref={inputRef}
              type="search"
              placeholder="Search servers by name, UUID, or IP..."
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              className="w-full bg-transparent outline-none text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400"
            />
          </div>
          <div className="max-h-80 overflow-y-auto">
            {loading && (
              <div className="p-4 text-sm text-gray-500">Searching…</div>
            )}
            {!loading && results.length === 0 && term.trim().length >= 3 && (
              <div className="p-4 text-sm text-gray-500">No results</div>
            )}
            {!loading && results.length > 0 && (
              <ul className="divide-y divide-gray-100 dark:divide-[#2d2d2d]">
                {results.map((r) => (
                  <li key={r.uuid}>
                    <Link
                      to={`/servers/${r.uuid}`}
                      onClick={onClose}
                      className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-[#2a2a2e]"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{r.name}</div>
                        <div className="text-xs text-gray-500 truncate">
                          {r.identifier} • {r.node}
                          {r.allocation ? ` • ${(r.allocation.ip_alias || r.allocation.ip)}:${r.allocation.port}` : ''}
                        </div>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="px-4 py-2 text-[11px] text-gray-500 border-t border-gray-100 dark:border-[#2d2d2d]">
            Type at least 3 characters • Press Esc to close
          </div>
        </div>
      </div>
    </div>
  )
}
